export interface PRDetails {
	url: string;
	title: string;
	createdAt: string;
	state?: 'open' | 'closed';
	body?: string;
	type?: 'issue_comment' | 'review_comment' | 'review' | 'review_body';
}

export interface PRStats {
	author: string;
	slackName: string;
	totalPRs: number;
	openPRs: number;
	avgDaysToClose: number;
	totalDaysOpen: number;
	commentsOnOthersPRs: number;
	prReviews: number;
	engineeringScore: number;
	seniorEngineeringScore: number;
	prsClosedInRange: number;
	linesAdded: number;
	linesRemoved: number;
	significantLines: number;
	details?: {
		prs: PRDetails[];
		reviews: PRDetails[];
		comments: PRDetails[];
	};
}

export interface PRStatsWithKey extends PRStats {
	key: string;
}

export interface FormulaComponent {
	variable: string;
	description: string;
}

export interface GradeScale {
	grade: string;
	percentile: number;
	color: string;
	description: string;
}

export interface FormulaExplanation {
	formula: string;
	components: FormulaComponent[];
	explanation: string;
	tips: string[];
	gradeScale: GradeScale[];
}

export const GRADE_SCALE: GradeScale[] = [
	{
		grade: 'A+',
		percentile: 95,
		color: '#52c41a',
		description: 'Top 5% - Outstanding performance',
	},
	{ grade: 'A', percentile: 90, color: '#73d13d', description: 'Top 10% - Excellent performance' },
	{
		grade: 'A-',
		percentile: 85,
		color: '#95de64',
		description: 'Top 15% - Very strong performance',
	},
	{ grade: 'B+', percentile: 75, color: '#bae637', description: 'Top 25% - Strong performance' },
	{ grade: 'B', percentile: 65, color: '#d3f261', description: 'Top 35% - Good performance' },
	{ grade: 'B-', percentile: 55, color: '#f4ffb8', description: 'Above average performance' },
	{ grade: 'C+', percentile: 45, color: '#fff566', description: 'Average performance' },
	{ grade: 'C', percentile: 35, color: '#ffd666', description: 'Below average performance' },
	{ grade: 'C-', percentile: 25, color: '#ffc069', description: 'Needs improvement' },
	{ grade: 'D+', percentile: 17.5, color: '#ffa940', description: 'Significantly below average' },
	{ grade: 'D', percentile: 10, color: '#ff7a45', description: 'Poor performance' },
	{
		grade: 'F',
		percentile: 0,
		color: '#ff4d4f',
		description: 'Bottom 10% - Critical improvement needed',
	},
];

export const SCORE_EXPLANATIONS: Record<
	'engineeringScore' | 'seniorEngineeringScore',
	FormulaExplanation
> = {
	engineeringScore: {
		formula: 'score = (2 * PRs + 3 * Reviews + 2 * Comments) / (1 + avgDaysToClose/7)',
		components: [
			{ variable: 'PRs', description: 'Total number of pull requests submitted' },
			{ variable: 'Reviews', description: 'Number of pull request reviews performed' },
			{ variable: 'Comments', description: "Number of comments made on other engineers' PRs" },
			{ variable: 'avgDaysToClose', description: 'Average number of days taken to close PRs' },
		],
		explanation:
			'The engineering score balances contribution through code changes (PRs) with collaboration (reviews and comments), while factoring in efficiency through PR closure time. Higher scores indicate strong overall engineering performance.',
		tips: [
			'Submit regular, well-scoped pull requests',
			'Actively participate in code reviews',
			"Provide constructive comments on others' PRs",
			'Focus on closing PRs efficiently by addressing feedback quickly',
			'Break down large changes into smaller, more manageable PRs',
		],
		gradeScale: GRADE_SCALE,
	},
	seniorEngineeringScore: {
		formula: 'score = (1 * PRs + 4 * Reviews + 3 * Comments) / (1 + avgDaysToClose/7)',
		components: [
			{ variable: 'PRs', description: 'Total number of pull requests submitted' },
			{ variable: 'Reviews', description: 'Number of pull request reviews performed' },
			{ variable: 'Comments', description: "Number of comments made on other engineers' PRs" },
			{ variable: 'avgDaysToClose', description: 'Average number of days taken to close PRs' },
		],
		explanation:
			'The senior engineering score emphasizes mentorship and code quality through increased weight on reviews and comments. It reflects the expectation that senior engineers spend more time guiding others and maintaining code quality.',
		tips: [
			"Prioritize reviewing others' code thoroughly",
			'Provide detailed, educational comments that help others learn',
			'Share knowledge through code review discussions',
			'Help unblock other engineers by providing timely reviews',
			'Balance coding with mentorship activities',
		],
		gradeScale: GRADE_SCALE,
	},
};
